<template>
  <div class="vip-subscribe-page">
    <div class="page-header">
      <h1>开通VIP会员</h1>
      <p class="subtitle">尊享会员特权，畅读精彩漫画</p>
    </div>
    
    <YouthModeBanner :blocks-purchase="true" @status="onYouthStatus" />

    <div class="vip-benefits">
      <h2>会员特权</h2>
      <div class="benefits-list">
        <div class="benefit-item">
          <div class="benefit-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
          </div>
          <div class="benefit-text">免费观看所有付费漫画</div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3 6 6 1-4.5 4.4 1 6.6-5.5-3-5.5 3 1-6.6L3 9l6-1z"/></svg>
          </div>
          <div class="benefit-text">新漫画优先更新</div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M3 12h18M12 8v13"/><path d="M12 8S9 3 6.5 4.5 8 8 12 8s5.5 0 5.5-3.5S12 8 12 8z"/></svg>
          </div>
          <div class="benefit-text">每月赠送金币</div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l4 5 5-7 5 7 4-5v11H3z"/></svg>
          </div>
          <div class="benefit-text">专属VIP标识</div>
        </div>
      </div>
    </div>
    
    <div class="vip-packages">
      <h2>选择套餐</h2>
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>加载中...</p>
      </div>
      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
        <button @click="fetchVipPackages" class="retry-btn">重试</button>
      </div>
      <div v-else-if="packages.length === 0" class="empty-message">
        <p>暂无可用的VIP套餐</p>
      </div>
      <div v-else class="packages-list">
        <div 
          v-for="pkg in packages" 
          :key="pkg.id" 
          class="package-item"
          :class="{ 'selected': selectedPackage && selectedPackage.id === pkg.id }"
          @click="selectPackage(pkg)"
        >
          <div class="package-header">
            <h3>{{ pkg.name }}</h3>
            <span class="duration">{{ pkg.duration }}天</span>
          </div>
          <div class="package-price">
            <span class="price-symbol">¥</span>
            <span class="price-value">{{ pkg.price }}</span>
          </div>
          <div class="package-privileges">
            <div v-for="(privilege, index) in pkg.privileges_array" :key="index" class="privilege-item">
              <span class="check-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <span>{{ privilege }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="package-info" v-if="selectedPackage">
      <h3>套餐详情</h3>
      <div class="info-content">
        <p><strong>套餐名称：</strong>{{ selectedPackage.name }}</p>
        <p><strong>有效期：</strong>{{ selectedPackage.duration }}天</p>
        <p><strong>价格：</strong>¥{{ selectedPackage.price }}</p>
        <p><strong>特权：</strong></p>
        <ul>
          <li v-for="(privilege, index) in selectedPackage.privileges_array" :key="index">
            {{ privilege }}
          </li>
        </ul>
      </div>

    </div>
    
    <div class="payment-section">
      <button 
        class="payment-btn" 
        :disabled="!selectedPackage || youthBlocked" 
        @click="handlePayment"
      >
        {{ youthBlocked ? '青少年模式下无法开通' : '立即开通' }}
      </button>
      <p class="payment-tips">开通即表示同意<router-link to="/agreement">《用户协议》</router-link></p>
    </div>
    
    <div class="login-reminder" v-if="!isLoggedIn">
      <p>请先<router-link to="/profile">登录</router-link>后查看更多VIP信息</p>
    </div>
    
    <!-- 支付弹窗 -->
    <PaymentModal
      v-if="showPaymentModal"
      :title="selectedPackage ? selectedPackage.name : ''"
      :price="selectedPackage ? selectedPackage.price : 0"
      :orderId="`VIP_${Date.now()}`"
      @close="showPaymentModal = false"
      @payment-success="handlePaymentSuccess"
      @payment-timeout="handlePaymentTimeout"
      @payment-failure="handlePaymentFailure"
    />
  </div>
</template>

<script>
import { getVipPackages } from '../api/index';
import PaymentModal from '../components/PaymentModal.vue';
import YouthModeBanner from '../components/YouthModeBanner.vue';
import youthModeManager from '@/utils/youthMode';

export default {
  name: 'VipSubscribePage',
  components: {
    PaymentModal,
    YouthModeBanner
  },
  data() {
    return {
      packages: [],
      selectedPackage: null,
      loading: true,
      error: null,
      isLoggedIn: false,
      showPaymentModal: false,
      youthBlocked: false
    }
  },
  created() {
    this.checkLoginStatus();
    this.fetchVipPackages();
  },
  methods: {
    onYouthStatus(status) {
      this.youthBlocked = !!status.enabled;
    },
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
    },
    async fetchVipPackages() {
      this.loading = true;
      this.error = null;
      
      try {
        console.log('[VipSubscribe] 获取VIP套餐列表');
        const res = await getVipPackages();
        
        if (res.data && res.data.code === 1) {
          this.packages = res.data.data;
          console.log('[VipSubscribe] VIP套餐获取成功:', this.packages);
          
          // 如果有套餐，默认选中第一个
          if (this.packages.length > 0) {
            this.selectedPackage = this.packages[0];
          }
        } else {
          this.error = res.data.msg || '获取VIP套餐失败';
          console.error('[VipSubscribe] VIP套餐获取失败:', res.data);
        }
      } catch (e) {
        this.error = '网络错误，请稍后重试';
        console.error('[VipSubscribe] VIP套餐获取出错:', e);
      } finally {
        this.loading = false;
      }
    },
    selectPackage(pkg) {
      this.selectedPackage = pkg;
      console.log('[VipSubscribe] 选择套餐:', pkg.name);
    },
    async handlePayment() {
      if (!this.isLoggedIn) {
        alert('请先登录后再开通VIP');
        this.$router.push('/profile');
        return;
      }
      
      if (!this.selectedPackage) {
        alert('请选择VIP套餐');
        return;
      }

      // 青少年模式购买拦截
      const purchaseCheck = await youthModeManager.checkBeforePurchase();
      if (!purchaseCheck.allowed) {
        alert(purchaseCheck.message);
        return;
      }

      // 显示支付弹窗
      this.showPaymentModal = true;
      console.log('[VipSubscribe] 打开支付弹窗，套餐:', this.selectedPackage);
    },
    handlePaymentSuccess(paymentInfo) {
      console.log('[VipSubscribe] 支付成功:', paymentInfo);
      this.showPaymentModal = false;
    },
    handlePaymentTimeout() {
      console.log('[VipSubscribe] 支付超时');
      this.showPaymentModal = false;
    },
    handlePaymentFailure(failureInfo) {
      console.log('[VipSubscribe] 支付失败:', failureInfo);
      alert(failureInfo.message || '未检测到支付信息，充值失败');
    }
  }
}
</script>

<style scoped>
.vip-subscribe-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 20px;
  background: var(--bg);
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 32px;
  color: var(--text);
  margin-bottom: 10px;
}

.subtitle {
  font-size: 16px;
  color: var(--text-dim);
}

.vip-benefits {
  margin-bottom: 40px;
}

.vip-benefits h2 {
  font-size: 24px;
  color: var(--text);
  margin-bottom: 20px;
  text-align: center;
}

.benefits-list {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}

.benefit-item {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  flex: 1 1 200px;
  max-width: 220px;
  transition: transform var(--t-fast), border-color var(--t-fast);
}

.benefit-item:hover {
  transform: translateY(-5px);
  border-color: var(--brand-soft);
}

.benefit-icon {
  width: 36px;
  height: 36px;
  margin: 0 auto 15px;
  color: var(--brand);
  display: flex;
  align-items: center;
  justify-content: center;
}

.benefit-icon svg {
  width: 100%;
  height: 100%;
}

.benefit-text {
  font-size: 16px;
  color: var(--text);
}

.vip-packages {
  margin-bottom: 40px;
}

.vip-packages h2 {
  font-size: 24px;
  color: var(--text);
  margin-bottom: 20px;
  text-align: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message, .empty-message {
  text-align: center;
  padding: 30px;
  color: var(--danger);
}

.retry-btn {
  background: var(--brand);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
}

.retry-btn:hover {
  background: var(--brand-hover);
}

.packages-list {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
}

.package-item {
  background: var(--surface-2);
  border-radius: 12px;
  padding: 25px;
  flex: 1 1 250px;
  max-width: 300px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid var(--border);
}

.package-item:hover {
  transform: translateY(-5px);
  border-color: var(--border-strong);
}

.package-item.selected {
  border-color: var(--brand);
  background: var(--brand-soft);
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.package-header h3 {
  font-size: 20px;
  color: var(--text);
  margin: 0;
}

.duration {
  background: var(--brand-soft);
  color: var(--brand);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
}

.package-price {
  font-size: 32px;
  color: var(--brand);
  margin-bottom: 20px;
  font-weight: bold;
}

.price-symbol {
  font-size: 20px;
  vertical-align: super;
}

.package-privileges {
  border-top: 1px solid var(--border);
  padding-top: 15px;
}

.privilege-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-dim);
}

.check-icon {
  color: var(--brand);
  margin-right: 8px;
  width: 15px;
  height: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.check-icon svg {
  width: 100%;
  height: 100%;
}

.package-info {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
}

.package-info h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--text);
  font-size: 20px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 10px;
}

.info-content {
  margin-bottom: 20px;
  color: var(--text-dim);
}

.info-content p {
  margin: 10px 0;
}

.info-content ul {
  margin: 0;
  padding-left: 20px;
}

.info-content li {
  margin-bottom: 5px;
}

.info-notice {
  background: var(--brand-soft);
  border: 1px solid var(--border);
  padding: 15px;
  border-radius: 8px;
  color: var(--text-dim);
  font-size: 14px;
  text-align: center;
}

.info-notice p {
  margin: 5px 0;
}

.payment-section {
  text-align: center;
  margin-bottom: 30px;
}

.payment-btn {
  background: var(--brand);
  color: white;
  border: none;
  padding: 12px 40px;
  border-radius: 25px;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.3s;
}

.payment-btn:hover:not(:disabled) {
  background: var(--brand-hover);
}

.payment-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
}

.payment-tips {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-dim);
}

.payment-tips a {
  color: var(--brand);
  text-decoration: none;
}

.payment-tips a:hover {
  text-decoration: underline;
}

.login-reminder {
  text-align: center;
  padding: 20px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  margin-top: 30px;
}

.login-reminder p {
  margin: 0;
  color: var(--text-dim);
}

.login-reminder a {
  color: var(--brand);
  font-weight: bold;
  text-decoration: none;
}

.login-reminder a:hover {
  text-decoration: underline;
}
</style> 